// Authentication Flow - Constants & Configuration
// Animation specs, step configs, gradients

// ============================================================================
// Animation Configuration
// ============================================================================

export const springConfig = {
  // Standard spring for most animations
  default: { type: 'spring' as const, stiffness: 300, damping: 30 },

  // Bouncy spring for celebratory/attention-grabbing animations
  bouncy: { type: 'spring' as const, stiffness: 400, damping: 20 },

  // Gentle spring for subtle movements
  gentle: { type: 'spring' as const, stiffness: 200, damping: 25 },

  // Stiff spring for quick, snappy movements
  stiff: { type: 'spring' as const, stiffness: 500, damping: 35 },

  // Extra bouncy for celebrations
  celebration: { type: 'spring' as const, stiffness: 300, damping: 15 },
};

export const transitionConfig = {
  // Page/content transitions
  fade: { duration: 0.2, ease: 'easeInOut' },
  fadeIn: { duration: 0.3, ease: 'easeOut' },
  fadeOut: { duration: 0.2, ease: 'easeIn' },

  // Stagger delays for lists
  stagger: 0.1,
  staggerFast: 0.05,
  staggerSlow: 0.15,
};

export const animationVariants = {
  // Page entrance
  pageEnter: {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: springConfig.default,
    },
  },

  // Card entrance with stagger
  cardStagger: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  },

  // Form field entrance
  fieldEnter: {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: springConfig.default,
    },
  },

  // Card entrance
  cardEnter: {
    hidden: { opacity: 0, y: 20, scale: 0.98 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: springConfig.default,
    },
    exit: { opacity: 0, y: -10, scale: 0.98, transition: { duration: 0.2 } },
  },

  // Step transition (forward)
  stepForward: {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: springConfig.default,
    },
    exit: { opacity: 0, x: -50, transition: { duration: 0.2 } },
  },

  // Step transition (backward)
  stepBackward: {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: springConfig.default,
    },
    exit: { opacity: 0, x: 50, transition: { duration: 0.2 } },
  },

  // Success celebration
  successPop: {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: springConfig.celebration,
    },
  },

  // Badge pop animation
  badgePop: {
    hidden: { opacity: 0, scale: 0 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: springConfig.bouncy,
    },
  },

  // Shake animation for errors
  shake: {
    shake: {
      x: [0, -10, 10, -10, 10, 0],
      transition: { duration: 0.4 },
    },
  },

  // Content swap
  contentSwap: {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.2 } },
    exit: { opacity: 0, y: -10, transition: { duration: 0.15 } },
  },
};

// ============================================================================
// Gradient Themes (Legacy - for backwards compatibility)
// ============================================================================

export const authGradients = {
  // Background gradients
  login: 'from-slate-50 via-blue-50/40 to-purple-50/30',
  register: 'from-slate-50 via-purple-50/30 to-blue-50/40',
  success: 'from-emerald-50/50 via-blue-50/30 to-purple-50/20',

  // Card border gradients
  cardBorder: 'from-blue-500/20 via-purple-500/20 to-blue-500/20',

  // Icon gradients
  iconPrimary: 'from-blue-500 to-purple-600',
  iconSuccess: 'from-emerald-400 to-emerald-600',
  iconClient: 'from-blue-500 to-cyan-500',
  iconLawyer: 'from-purple-500 to-pink-500',
};

// ============================================================================
// Theme-Specific Auth Configurations
// ============================================================================

export type AuthTheme = 'invol' | 'drboese';

export interface AuthThemeConfig {
  login: string;
  register: string;
  success: string;
  cardBorder: string;
  iconPrimary: string;
  iconPrimaryIsGradient: boolean;
  iconColor: string;
  progressActive: string;
  progressCompleted: string;
  link: string;
  linkHover: string;
  linkUnderline: string;
  radialGlow: string;
  orbPrimary: string;
  orbSecondary: string;
  buttonPrimary: string;
  buttonShadow: string;
}

export const authThemeConfigs: Record<AuthTheme, AuthThemeConfig> = {
  invol: {
    // Background gradients
    login: 'from-slate-50 via-blue-50/40 to-purple-50/30',
    register: 'from-slate-50 via-purple-50/30 to-blue-50/40',
    success: 'from-emerald-50/50 via-blue-50/30 to-purple-50/20',
    // Card border
    cardBorder: 'from-blue-500/20 via-purple-500/20 to-blue-500/20',
    // Icon styling
    iconPrimary: 'from-blue-500 to-purple-600',
    iconPrimaryIsGradient: true,
    iconColor: 'text-white',
    // Progress indicators
    progressActive: 'from-blue-500 to-purple-500',
    progressCompleted: 'bg-blue-400',
    // Links
    link: 'text-blue-600',
    linkHover: 'hover:text-blue-700',
    linkUnderline: 'bg-blue-600',
    // Animated background
    radialGlow: 'rgba(99, 102, 241, 0.08)',
    orbPrimary: 'rgba(99, 102, 241, 0.06)',
    orbSecondary: 'rgba(168, 85, 247, 0.05)',
    // Button styling
    buttonPrimary: 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white',
    buttonShadow: 'shadow-blue-500/25',
  },
  drboese: {
    // Background gradients - warm cream/yellow tones
    login: 'from-slate-50 via-amber-50/30 to-yellow-50/20',
    register: 'from-slate-50 via-yellow-50/20 to-amber-50/30',
    success: 'from-emerald-50/50 via-yellow-50/30 to-amber-50/20',
    // Card border - golden gradient
    cardBorder: 'from-yellow-400/20 via-amber-400/20 to-yellow-400/20',
    // Icon styling - solid yellow with black icon
    iconPrimary: 'bg-[rgb(255,211,52)]',
    iconPrimaryIsGradient: false,
    iconColor: 'text-black',
    // Progress indicators
    progressActive: 'from-yellow-400 to-amber-500',
    progressCompleted: 'bg-yellow-400',
    // Links
    link: 'text-yellow-600',
    linkHover: 'hover:text-yellow-700',
    linkUnderline: 'bg-yellow-600',
    // Animated background
    radialGlow: 'rgba(255, 211, 52, 0.08)',
    orbPrimary: 'rgba(255, 211, 52, 0.06)',
    orbSecondary: 'rgba(234, 179, 8, 0.05)',
    // Button styling
    buttonPrimary: 'bg-gradient-to-r from-[rgb(255,211,52)] to-amber-400 hover:from-amber-400 hover:to-amber-500 text-black',
    buttonShadow: 'shadow-yellow-500/25',
  },
};

// Logo configuration per theme
export interface LogoConfig {
  type: 'text' | 'image';
  text?: string;
  gradient?: string;
  src?: string;
  alt?: string;
  height?: string;
}

export const authLogoConfigs: Record<AuthTheme, LogoConfig> = {
  invol: {
    type: 'text',
    text: 'Invol',
    gradient: 'from-blue-600 to-purple-600',
  },
  drboese: {
    type: 'image',
    src: '/static/documents/images/drboese-logo.png',
    alt: 'Kanzlei Dr. Böse',
    height: 'h-12',
  },
};

// Footer text per theme
export const authFooterTexts: Record<AuthTheme, string> = {
  invol: 'EU261 Flight Compensation Platform',
  drboese: 'Kanzlei Dr. Böse · EU261 Platform',
};

// Helper to get theme config with fallback
export function getAuthThemeConfig(theme: AuthTheme): AuthThemeConfig {
  return authThemeConfigs[theme] || authThemeConfigs.invol;
}

// Helper to resolve logo URL for development
export function resolveAuthLogoUrl(src: string | undefined): string | undefined {
  if (!src) return undefined;
  if (src.startsWith('http://') || src.startsWith('https://')) return src;
  // In development, static files are served from backend (port 8000)
  // Check if we're on localhost:3000 (dev frontend)
  if (src.startsWith('/static/') && typeof window !== 'undefined' && window.location.port === '3000') {
    return `http://localhost:8000${src}`;
  }
  return src;
}

// ============================================================================
// Confetti Configuration
// ============================================================================

export const confettiConfig = {
  particleCount: 60,
  spread: 70,
  startVelocity: 30,
  decay: 0.95,
  gravity: 0.8,
  colors: ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ec4899'],
  shapes: ['square', 'circle'] as const,
};
